/**
 * Display labels for a trail's answering modes.
 *
 * DISPLAY ONLY. `trail.modes` keeps the raw values the backend stores and keys
 * on ("fast", "faststage", "guided" — the capture gate compares those exact
 * strings), so every filter, comparison and API call keeps using the raw value;
 * only the text a person reads goes through here.
 *
 * LOWER CASE, AND SHOWN AS WRITTEN. The overlay header reads "hybrid +
 * diagnostic" in its meta line (product · modes · steps), and
 * /learning's Mode column and chips used to pass the same text through CSS
 * `capitalize`, which made the same trail read "Hybrid + Diagnostic" there.
 * The labels are descriptive words, not names — no caller may re-case them.
 */
const MODE_LABELS = {
  fast: "hybrid",
  faststage: "hybrid",
  guided: "diagnostic",
};

/** One mode's label; an unknown mode is shown as it is. */
export function modeLabel(mode) {
  const raw = String(mode ?? "").trim();
  return MODE_LABELS[raw.toLowerCase()] || raw;
}

/** "hybrid + diagnostic" for ["fast", "guided"]. Labels that collapse onto the
 *  same text ("fast" and "faststage") are shown once. "" for none. */
export function modesLabel(modes) {
  const seen = [];
  (Array.isArray(modes) ? modes : []).forEach((m) => {
    const label = modeLabel(m);
    if (label && !seen.includes(label)) seen.push(label);
  });
  return seen.join(" + ");
}

/** Raw values of the fast (no runbook walk) answering path. */
const FAST_MODES = ["fast", "faststage"];
/** Raw value of the diagnostic (runbook walk) answering path. */
const DIAGNOSTIC_MODES = ["guided"];

/** Raw modes, trimmed and lower-cased, blanks dropped. */
function normalizedModes(modes) {
  return (Array.isArray(modes) ? modes : [])
    .map((m) => String(m ?? "").trim().toLowerCase())
    .filter(Boolean);
}

/** True when every mode is a fast one — the conversation never walked a
 *  runbook. False for no modes at all (nothing is known). */
export function isFastOnly(modes) {
  const list = normalizedModes(modes);
  return list.length > 0 && list.every((m) => FAST_MODES.includes(m));
}

/** True when ANY mode is a diagnostic one — the conversation walked a runbook
 *  at least once, mixed with hybrid turns or not. */
export function hasDiagnosticMode(modes) {
  return normalizedModes(modes).some((m) => DIAGNOSTIC_MODES.includes(m));
}
